

import React, { useState, useEffect } from "react";
import api from "../../api/api"; // Make sure api.js exists and exports OrderVsDelivery function
import Button from "../../components/ui/Button";
import Heading from "../../components/ui/Heading"; 
import SearchInput from "../../components/ui/SearchInput";
import { getSessionUsername } from "../../utils/sessionUser";

/* ------------------ Data Transformation ------------------ */
const transformData = (table) =>
  table.map((item) => {
    const orderDate = item.podate?.split("T")[0] || "";
    const deliveryDate = item.DeliveryDate?.split("T")[0] || "";

    return {
      OrderId: item.poid,
      OrderDate: orderDate,
      DeliveryDate: deliveryDate,
      CustomerName: item.vname,
      CustPhone: item.mobileno,
      ItemName: item.Partdesc,
      Quantity: item.qty,
      misinformation:
        orderDate && deliveryDate ? deliveryDate < orderDate : false,
    };
  });

/* ------------------ Components ------------------ */

/* Misinformation Report */
const MisinformationReport = ({ data }) => {
  const flaggedItems = data.filter((item) => item.misinformation);

  if (flaggedItems.length === 0)
    return (
      <div className="no-misinformation">
        ✅ No misinformation found. All deliveries are on schedule!
      </div>
    );

  return (
    <table className="report-table">
      <thead>
        <tr>
          <th>Order ID</th>
          <th>Item Name</th>
          <th>Order Date</th>
          <th>Delivery Date</th>
        </tr>
      </thead>
      <tbody>
        {flaggedItems.map((item, index) => (
          <tr key={index}>
            <td>{item.OrderId}</td>
            <td>{item.ItemName}</td>
            <td>{item.OrderDate}</td>
            <td>{item.DeliveryDate}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

/* Date-wise Delivery List */
const DateWiseDeliveryList = ({ data }) => {
  const [selectedOrderId, setSelectedOrderId] = useState(null);

  const deliveriesByDate = data.reduce((acc, item) => {
    if (!acc[item.DeliveryDate]) acc[item.DeliveryDate] = [];
    acc[item.DeliveryDate].push(item);
    return acc;
  }, {});

  const sortedDates = Object.keys(deliveriesByDate).sort();

  return (
    <div>
      {sortedDates.map((date) => (
        <div key={date} className="date-group">
          <h3>🗓️ {date}</h3>
          <ul className="delivery-list">
            {deliveriesByDate[date].map((item, index) => (
              <li
                key={index}
                onClick={() =>
                  setSelectedOrderId(
                    selectedOrderId === item.OrderId ? null : item.OrderId
                  )
                }
                className={`delivery-item ${
                  selectedOrderId === item.OrderId ? "selected-item" : ""
                }`}
              >
                <div>
                  <strong>Order #{item.OrderId}</strong>
                  {selectedOrderId === item.OrderId && (
                    <span className="customer-info">
                      <br />
                      Customer: {item.CustomerName}
                      <br />
                      Phone: {item.CustPhone}
                    </span>
                  )}
                  : {item.Quantity} × {item.ItemName} (Ordered on{" "}
                  {item.OrderDate})
                </div>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
};

/* Item-wise Summary */
const ItemWiseSummary = ({ data }) => {
  const [showPopup, setShowPopup] = useState(false);
  const [popupData, setPopupData] = useState([]);
  const [popupTitle, setPopupTitle] = useState("");
  const [itemSearchTerm, setItemSearchTerm] = useState("");

  const summary = data.reduce((acc, item) => {
    if (!acc[item.ItemName])
      acc[item.ItemName] = { totalQuantity: 0, orderCount: 0, orders: [] };
    acc[item.ItemName].totalQuantity += item.Quantity;
    acc[item.ItemName].orderCount += 1;
    acc[item.ItemName].orders.push(item);
    return acc;
  }, {});

  const filteredItems = Object.keys(summary).filter((name) =>
    name.toLowerCase().includes(itemSearchTerm.toLowerCase())
  );
  const sortedItems = filteredItems.sort(
    (a, b) => summary[b].totalQuantity - summary[a].totalQuantity
  );

  const handleOrderClick = (itemName) => {
    setPopupData(summary[itemName].orders);
    setPopupTitle(`Orders for ${itemName}`);
    setShowPopup(true);
  };

  const closePopup = () => {
    setShowPopup(false);
    setPopupData([]);
  };

  return (
    <div>
      <div className="filter-item-container">
        <input
          type="text"
          placeholder="Filter items..."
          value={itemSearchTerm}
          onChange={(e) => setItemSearchTerm(e.target.value)}
        />
      </div>
      <table className="report-table">
        <thead>
          <tr>
            <th>Item Name</th>
            <th>Total Quantity</th>
            <th>Number of Orders</th>
          </tr>
        </thead>
        <tbody>
          {sortedItems.map((itemName) => (
            <tr key={itemName}>
              <td>{itemName}</td>
              <td
                className="clickable-cell"
                onClick={() => handleOrderClick(itemName)}
              >
                {summary[itemName].totalQuantity}
              </td>
              <td
                className="clickable-cell"
                onClick={() => handleOrderClick(itemName)}
              >
                {summary[itemName].orderCount}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {showPopup && (
        <div className="popup-overlay">
          <div className="popup-content">
            <button className="close-popup" onClick={closePopup}>
              &times;
            </button>
            <h3>{popupTitle}</h3>
            <table className="popup-table">
              <thead>
                <tr>
                  <th>Order ID</th>
                  <th>Customer Name</th>
                  <th>Mobile No</th>
                  <th>Delivery Date</th>
                </tr>
              </thead>
              <tbody>
                {popupData.map((order, index) => (
                  <tr key={index}>
                    <td>{order.OrderId}</td>
                    <td>{order.CustomerName}</td>
                    <td>{order.CustPhone}</td>
                    <td>{order.DeliveryDate}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

/* Date-wise Summary */
const DateWiseSummary = ({ data }) => {
  const summary = data.reduce((acc, item) => {
    if (!acc[item.DeliveryDate])
      acc[item.DeliveryDate] = { totalQuantity: 0, orderCount: 0 };
    acc[item.DeliveryDate].totalQuantity += item.Quantity;
    acc[item.DeliveryDate].orderCount += 1;
    return acc;
  }, {});

  const sortedDates = Object.keys(summary).sort();

  return (
    <table className="report-table">
      <thead>
        <tr>
          <th>Delivery Date</th>
          <th>Total Items</th>
          <th>Total Orders</th>
        </tr>
      </thead>
      <tbody>
        {sortedDates.map((date) => (
          <tr key={date}>
            <td>{date}</td>
            <td>{summary[date].totalQuantity}</td>
            <td>{summary[date].orderCount}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

/* Calendar View */
const CalendarView = ({ data }) => {
  const deliveriesByDate = data.reduce((acc, item) => {
    if (!acc[item.DeliveryDate]) acc[item.DeliveryDate] = 0;
    acc[item.DeliveryDate] += item.Quantity;
    return acc;
  }, {});

  const sortedDates = Object.keys(deliveriesByDate).sort();

  return (
    <ul className="calendar-list">
      {sortedDates.map((date) => (
        <li key={date}>
          {date} → {deliveriesByDate[date]} items
        </li>
      ))}
    </ul>
  );
};

/* Accordion */
const Accordion = ({ title, children, defaultOpen = false }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  return (
    <div className="accordion-item">
      <div className="accordion-header" onClick={() => setIsOpen(!isOpen)}>
        <h2>{title}</h2>
        <span className={`accordion-icon ${isOpen ? "open" : ""}`}>&#9660;</span>
      </div>
      <div className={`accordion-content ${isOpen ? "open" : ""}`}>{children}</div>
    </div>
  );
};

/* ------------------ Main Dashboard ------------------ */
const DeliveryMIS = ({ setView }) => {
  const [data, setData] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [misinformationFilter, setMisinformationFilter] = useState("all");
 
  useEffect(() => {
  const fetchData = async () => {
    try {
      const response = await api.OrderVsDelivery(getSessionUsername());
      console.log("API raw response:", response);

      // Extract the table directly
      const table = response?.Table || [];
      console.log("Extracted Table:", table);

      // Transform data
      const transformed = table.map((item) => ({
        OrderId: item.poid,
        OrderDate: item.podate?.split("T")[0] || "",
        DeliveryDate: item.DeliveryDate?.split("T")[0] || "",
        CustomerName: item.vname,
        CustPhone: item.mobileno,
        ItemName: item.Partdesc,
        Quantity: Number(item.qty) || 0,
        misinformation:
          item.podate && item.DeliveryDate
            ? item.DeliveryDate < item.podate
            : false,
      }));

      setData(transformed);
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  fetchData();
}, []);


  const filteredData = data.filter((item) => {
    const matchesSearch =
      (item.ItemName || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
      (item.CustomerName || "")
        .toLowerCase()
        .includes(searchTerm.toLowerCase());
    const matchesMisinformation =
      misinformationFilter === "all" ||
      (misinformationFilter === "true" && item.misinformation) ||
      (misinformationFilter === "false" && !item.misinformation);
    return matchesSearch && matchesMisinformation;
  });

  return (
    <div className="dashboard">
       <Heading> Interactive Order & Delivery Dashboard</Heading>

      {/* <div className="flex items-center gap-2 px-3 py-2 bg-white border-b">
  <Button
    onClick={() => setView("home")}
    className="flex items-center justify-center w-8 h-8 text-white bg-white-500 rounded-full hover:bg-blue-600"
  >
    ⬅
  </Button> */}

<div className="flex items-center gap-2 px-3 py-2 bg-white border-b">
        <Button
          onClick={() => setView("home")}
          className="flex items-center justify-center w-8 h-8 text-gray-700 bg-gray-100 rounded-full"
        >
          ⬅
        </Button>


  <SearchInput
    type="text"
          placeholder="Search by Item or Customer Name..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
  />
        <select
          value={misinformationFilter}
          onChange={(e) => setMisinformationFilter(e.target.value)}
        >
          <option value="all">Show All</option>
          <option value="true">Show Flagged Only</option>
          <option value="false">Show Correct Only</option>
        </select>
      </div>



      <Accordion title="📅 Calendar View" defaultOpen={true}>
        <CalendarView data={filteredData} />
      </Accordion>
      <Accordion title="⚠️ Misinformation Report">
        <MisinformationReport data={filteredData} />
      </Accordion>
      <Accordion title="🗓️ Date-wise Delivery List">
        <DateWiseDeliveryList data={filteredData} />
      </Accordion>
      <Accordion title="📦 Item-wise Summary">
        <ItemWiseSummary data={filteredData} />
      </Accordion>
      <Accordion title="📊 Date-wise Summary">
        <DateWiseSummary data={filteredData} />
      </Accordion>

      {/* ------------------ Styles ------------------ */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;600&display=swap');
        .dashboard{ font-family:'Poppins',sans-serif; max-width:1200px; margin:20px auto; padding:20px; background:#fff; border-radius:12px; box-shadow:0 4px 20px rgba(0,0,0,0.05);}
        h1{ text-align:center; font-size:2.5em; color:#4a148c; border-bottom:3px solid #7e57c2; padding-bottom:10px;}
        .filter-container{ display:flex; flex-wrap:wrap; gap:15px; justify-content:center; background:#e8eaf6; padding:20px; border-radius:8px; margin-bottom:20px;}
        .filter-container input,.filter-container select{ padding:10px 15px; border:2px solid #c5cae9; border-radius:8px;}
        .accordion-item{ background:#f8f9fa; border:1px solid #e0e0e0; border-radius:8px; margin-bottom:20px; overflow:hidden;}
        .accordion-header{ display:flex; justify-content:space-between; align-items:center; padding:20px; background:#e8eaf6; cursor:pointer;}
        .accordion-icon{ font-size:1.5em; transition:transform .3s;}
        .accordion-icon.open{ transform:rotate(180deg);}
        .accordion-content{ max-height:0; overflow:hidden; transition:max-height .3s,padding .3s;}
        .accordion-content.open{ max-height:1000px; padding:20px;}
        .report-table{ width:100%; border-collapse:collapse; margin-top:15px;}
        .report-table th,.report-table td{ padding:12px; border-bottom:1px solid #ddd;}
        .report-table th{ background:#e8eaf6; color:#3949ab;}
        .report-table tbody tr:nth-child(odd){ background:#f5f5f5;}
        .report-table tbody tr:hover{ background:#e3f2fd;}
        .delivery-list{ list-style:none; padding:0;}
        .delivery-item{ background:#fff; margin-bottom:8px; padding:12px; border-radius:6px; border-left:5px solid #673ab7; box-shadow:0 1px 3px rgba(0,0,0,0.1);}
        .delivery-item.selected-item{ background:#e3f2fd; border-left-color:#3949ab;}
        .customer-info{ font-size:.9em; color:#555;}
        .no-misinformation{ background:#e8f5e9; color:#2e7d32; border:1px solid #c8e6c9; text-align:center; padding:15px; border-radius:8px; font-weight:600;}
        .popup-overlay{ position:fixed; top:0; left:0; width:100%; height:100%; background:rgba(0,0,0,.5); display:flex; justify-content:center; align-items:center; z-index:1000;}
        .popup-content{ background:#fff; padding:30px; border-radius:12px; max-width:90%; max-height:90%; overflow-y:auto; position:relative;}
        .close-popup{ position:absolute; top:10px; right:15px; font-size:2em; background:none; border:none; cursor:pointer; color:#757575;}
        .clickable-cell{ cursor:pointer; color:#3949ab; text-decoration:underline;}
      `}</style>
    </div>
  );
};

export default DeliveryMIS;
